/**
 * Pure view-model helpers for the Connected Health screens (Figma 11128:25184).
 *
 * Everything the pages render is derived here from the core-lambda payload
 * (`GET /api/v4/health/connections`), so the rules — what "On" means, which
 * providers are clickable, how a sync time reads — are unit-testable apart
 * from React.
 */

export const STATUS = {
  CONNECTED: "CONNECTED",
  PARTIAL: "PARTIAL",
  NEEDS_ATTENTION: "NEEDS_ATTENTION",
  DISCONNECTED: "DISCONNECTED",
};

export const GRANT = { GRANTED: "GRANTED", DENIED: "DENIED" };

export const CATEGORY_STATE = { ON: "on", PARTIAL: "partial", OFF: "off" };

/** Providers that connect through OAuth (WHOOP, Oura) — not built yet. */
const OAUTH_FALLBACK = new Set(["whoop", "oura"]);

export const isComingSoon = (provider) =>
  provider?.connection_kind
    ? provider.connection_kind === "OAUTH"
    : OAUTH_FALLBACK.has(provider?.provider);

/** A connection row the member can manage: present and not disconnected. */
export const isActiveConnection = (connection) =>
  !!connection && connection.status !== STATUS.DISCONNECTED;

export const connectionFor = (connections, providerKey) =>
  (connections || []).find((c) => c?.provider === providerKey) || null;

const parse = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "just now", "5 minutes ago", "2 hours ago", "Yesterday", "3 days ago"… */
export const relativeTime = (iso, now = new Date()) => {
  const d = parse(iso);
  if (!d) return "";
  const minutes = Math.floor((now - d) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} ${months === 1 ? "month" : "months"} ago`;
  const years = Math.floor(days / 365);
  return `${years} ${years === 1 ? "year" : "years"} ago`;
};

/** "Today, 8:43 AM" / "Yesterday, 8:43 AM" / "Sep 14, 8:43 AM" / "Never". */
export const syncedAtLabel = (iso, now = new Date()) => {
  const d = parse(iso);
  if (!d) return "Never";
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const days = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (days === 0) return `Today, ${time}`;
  if (days === 1) return `Yesterday, ${time}`;
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${date}, ${time}`;
};

/** The one-line status under a provider on the list screen. */
export const providerStatusLine = (provider, connection, now = new Date()) => {
  if (isComingSoon(provider)) return "Coming soon";
  if (!isActiveConnection(connection)) return "Not connected";
  if (connection.status === STATUS.NEEDS_ATTENTION) return "Needs attention";
  const ago = relativeTime(connection.last_successful_sync_at, now);
  return ago ? `Connected · Last synced ${ago}` : "Connected";
};

/** The Status row on the detail screen. */
export const statusLabel = (connection) => {
  if (!isActiveConnection(connection)) return "Not connected";
  switch (connection.status) {
    case STATUS.CONNECTED:
      return "Connected";
    case STATUS.PARTIAL:
      return "Partially connected";
    case STATUS.NEEDS_ATTENTION:
      return "Needs attention";
    default:
      return "Connected";
  }
};

/** The History Access row. `GRANTED` is "Full Access" (Figma). */
export const historyAccessLabel = (state) => {
  switch (state) {
    case "GRANTED":
      return "Full Access";
    case "DENIED":
      return "Not allowed";
    case "NOT_REQUESTED":
      return "Not requested";
    default:
      return "Unknown";
  }
};

/**
 * A display category is On when every family it groups is authorized, Partly
 * on when some are, Off otherwise (core-lambda `health_module/copy.py`).
 */
export const categoryState = (category, authorizedFamilies) => {
  const families = category?.families || [];
  const granted = new Set(authorizedFamilies || []);
  const on = families.filter((f) => granted.has(f)).length;
  if (families.length > 0 && on === families.length) return CATEGORY_STATE.ON;
  if (on > 0) return CATEGORY_STATE.PARTIAL;
  return CATEGORY_STATE.OFF;
};

export const categoryStateLabel = (state) =>
  state === CATEGORY_STATE.ON
    ? "On"
    : state === CATEGORY_STATE.PARTIAL
      ? "Partly on"
      : "Off";

/** The `grants` body that turns every family of a category on or off. */
export const grantsFor = (category, turnOn) =>
  Object.fromEntries(
    (category?.families || []).map((f) => [f, turnOn ? GRANT.GRANTED : GRANT.DENIED])
  );

/** A readable message for a failed call; the backend error codes are not member copy. */
export const errorMessage = (error, fallback) => {
  const status = error?.response?.status;
  if (status === 409) return "This provider isn't connected any more. Refreshing…";
  if (status === 404) return "We couldn't find that connection.";
  return fallback;
};
